/**
 * postMessage protocol between Joyia Web (the host shell) and an app it embeds in an
 * iframe (KnowledgeFlow today; Deploy, Control Center, ... next).
 *
 * Paths on the wire are always APP paths (`pathname + search` inside the iframe). The host
 * owns the translation to its own URL space, so an embedded app never needs to know where
 * it is mounted.
 *
 * Every receiver MUST verify both `event.origin` and `event.source`. Authentication
 * messages are only for explicitly trusted first-party apps, never arbitrary frames.
 */

export type EmbedTheme = "light" | "dark";

/**
 * Brand palette the host is rendering in — a second axis, orthogonal to {@link EmbedTheme}.
 *
 * Carried so an embedded app CAN follow the host's brand, but note that none does yet: an
 * embedded app inside a branded shell still renders its own palette. That is not an oversight
 * to fix here — the apps do not currently share a palette at all (some are stock shadcn, others
 * have their own accent), so each one needs its own brand block written before consuming this
 * means anything. Wiring the transport now means that later work needs no protocol change.
 *
 * Kept as a string union mirroring `Brand` in Joyia Web's `src/lib/brand.ts` rather than
 * importing it: this package must not depend on a consumer.
 */
export type EmbedBrand = "corporate" | "mediaset";

/**
 * UI language the host is rendering in — a third axis beside theme and brand.
 *
 * Mirrors `supportedLanguages` in Joyia Web's `src/lib/i18n.ts` rather than importing it, for the
 * same reason as {@link EmbedBrand}: this package must not depend on a consumer. An unknown value
 * is rejected by {@link parseEmbedMessage}, so an app always receives a language it can act on.
 *
 * As with brand, this is TRANSPORT only: forwarding the language does not translate an embedded
 * app, which must consume it and have the translations to switch to.
 */
export type EmbedLanguage = "en" | "de" | "it" | "es" | "pt";

/** Languages a host may announce. Exported so a receiver can validate without a copy. */
export const EMBED_LANGUAGES: readonly EmbedLanguage[] = ["en", "de", "it", "es", "pt"];

/**
 * `theme-change` keeps its historical name: Tribes' ThemeProvider already listens for it,
 * so renaming would silently break that consumer.
 */
export type EmbedMessage =
  | { type: "joyia:embed:navigate"; path: string } // host → app
  | { type: "joyia:embed:route"; path: string } // app → host
  | { type: "theme-change"; theme: EmbedTheme } // host → app
  | { type: "joyia:embed:request-theme" } // app → host
  // Brand mirrors the theme pair exactly. Unlike `theme-change` these get the namespaced
  // prefix, since no existing consumer constrains their names.
  | { type: "joyia:embed:brand-change"; brand: EmbedBrand } // host → app
  | { type: "joyia:embed:request-brand" } // app → host
  | { type: "joyia:embed:language-change"; language: EmbedLanguage } // host → app
  | { type: "joyia:embed:request-language" } // app → host
  | { type: "joyia:embed:auth-request"; requestId: string; force: boolean } // app → host
  | { type: "joyia:embed:auth-response"; requestId: string; token: string | null } // host → app
  | { type: "joyia:embed:logout" } // either direction
  // app → host: the user's server-side preferences (persona, theme, language) changed, so the
  // host must refetch /me instead of serving its cached copy.
  | { type: "joyia:embed:preferences-changed" }
  | { type: "joyia:embed:open"; url: string } // app → host
  | { type: "joyia:embed:shortcut"; shortcut: "palette" } // app → host
  | { type: "joyia:embed:invalidate"; topic: "inbox" }; // either direction

/** Narrows untrusted `event.data`. Returns null for anything that is not a well-formed message. */
export function parseEmbedMessage(data: unknown): EmbedMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const msg = data as Record<string, unknown>;
  switch (msg.type) {
    case "joyia:embed:navigate":
    case "joyia:embed:route":
      // App-relative only: an absolute or protocol-relative (`//host`) path could steer a
      // router off-app.
      return typeof msg.path === "string" && msg.path.startsWith("/") && !msg.path.startsWith("//")
        ? { type: msg.type, path: msg.path }
        : null;
    case "theme-change":
      return msg.theme === "light" || msg.theme === "dark"
        ? { type: "theme-change", theme: msg.theme }
        : null;
    case "joyia:embed:request-theme":
      return { type: "joyia:embed:request-theme" };
    case "joyia:embed:brand-change":
      return msg.brand === "corporate" || msg.brand === "mediaset"
        ? { type: "joyia:embed:brand-change", brand: msg.brand }
        : null;
    case "joyia:embed:request-brand":
      return { type: "joyia:embed:request-brand" };
    case "joyia:embed:language-change":
      return EMBED_LANGUAGES.includes(msg.language as EmbedLanguage)
        ? { type: "joyia:embed:language-change", language: msg.language as EmbedLanguage }
        : null;
    case "joyia:embed:request-language":
      return { type: "joyia:embed:request-language" };
    case "joyia:embed:auth-request":
      return typeof msg.requestId === "string" && msg.requestId.length > 0 && typeof msg.force === "boolean"
        ? { type: msg.type, requestId: msg.requestId, force: msg.force }
        : null;
    case "joyia:embed:auth-response": {
      if (typeof msg.requestId !== "string" || msg.requestId.length === 0) return null;
      /*
       * Narrowed through a local rather than inline. The inline form relied on
       * `msg.token === null` narrowing `unknown`, which it only does under
       * `strictNullChecks` — and packages/file-viewer compiles with `strict: false`, so
       * there `msg.token` stayed `unknown` and would not assign to `string | null`.
       * A consumer's strictness must not decide whether this shared parser compiles.
       */
      const token = typeof msg.token === "string" && msg.token.length > 0 ? msg.token : null;
      if (token === null && msg.token !== null) return null;
      return { type: msg.type, requestId: msg.requestId, token };
    }
    case "joyia:embed:logout":
      return { type: msg.type };
    case "joyia:embed:preferences-changed":
      return { type: "joyia:embed:preferences-changed" };
    case "joyia:embed:open": {
      if (typeof msg.url !== "string") return null;
      try {
        const parsed = new URL(msg.url);
        return parsed.protocol === "http:" || parsed.protocol === "https:"
          ? { type: "joyia:embed:open", url: msg.url }
          : null;
      } catch {
        return null;
      }
    }
    case "joyia:embed:shortcut":
      return msg.shortcut === "palette"
        ? { type: "joyia:embed:shortcut", shortcut: "palette" }
        : null;
    case "joyia:embed:invalidate":
      return msg.topic === "inbox"
        ? { type: "joyia:embed:invalidate", topic: "inbox" }
        : null;
    default:
      return null;
  }
}

/** `https://a.example/x/` → `https://a.example`; null for an empty or unparsable URL. */
export function originOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** Query params the host appends to the iframe's initial URL. */
export const EMBED_PARAM = "embedded";
export const EMBED_THEME_PARAM = "theme";
export const EMBED_BRAND_PARAM = "brand";
export const EMBED_LANGUAGE_PARAM = "lang";

/**
 * True when rendered inside the host shell. `?embedded=1` covers the first paint and the
 * case where `window.top` is unreadable; the frame check covers in-app navigations that
 * dropped the query string.
 */
export function isEmbedded(): boolean {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).get(EMBED_PARAM) === "1") return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Theme the host passed on the initial URL, if any. */
export function initialEmbedTheme(): EmbedTheme | undefined {
  if (typeof window === "undefined") return undefined;
  const theme = new URLSearchParams(window.location.search).get(EMBED_THEME_PARAM);
  return theme === "light" || theme === "dark" ? theme : undefined;
}

/** Brand the host passed on the initial URL, if any. Covers the app's first paint. */
export function initialEmbedBrand(): EmbedBrand | undefined {
  if (typeof window === "undefined") return undefined;
  const brand = new URLSearchParams(window.location.search).get(EMBED_BRAND_PARAM);
  return brand === "corporate" || brand === "mediaset" ? brand : undefined;
}

/** Language the host passed on the initial URL, if any. */
export function initialEmbedLanguage(): EmbedLanguage | undefined {
  if (typeof window === "undefined") return undefined;
  const language = new URLSearchParams(window.location.search).get(EMBED_LANGUAGE_PARAM);
  return EMBED_LANGUAGES.includes(language as EmbedLanguage)
    ? (language as EmbedLanguage)
    : undefined;
}
